import { activateOPButton, encodeOPSaveRequest } from '@1password/save-button'
import { Button } from '@viko/transcript-ui'
import { Copy } from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'
import { type RecoveryDetails, recoverySaveRequest, recoveryText } from '../lib/recovery-save.js'

/**
 * The 1Password extension's save button contract (docs/research/onepassword-recovery.md): the
 * extension finds `data-onepassword-save-button`, reads the encoded request from its value and
 * enables it. `extension` is 'ready' only once the extension enabled the button.
 */
export function useOnePasswordSave(request: string) {
  const button = useRef<HTMLButtonElement>(null)
  // 'missing': extension never enabled the button; 'silent': it was enabled but a click did nothing.
  const [extension, setExtension] = useState<'waiting' | 'ready' | 'missing' | 'silent'>('waiting')
  const clickTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(() => {
    const el = button.current
    if (!el) return
    // React must not overwrite extension-owned disabled state on incidental rerenders.
    el.disabled = true
    el.value = request
    setExtension('waiting')
    activateOPButton()
    // The extension's content script can load after this component mounts, and it only scans on
    // the activation event: announce again a few times, and whenever the tab regains focus.
    const timers = [300, 1000, 2000].map((ms) => setTimeout(activateOPButton, ms))
    const giveUp = setTimeout(
      () => setExtension((s) => (s !== 'waiting' ? s : el.disabled ? 'missing' : 'ready')),
      3500,
    )
    const sync = () => {
      if (!el.disabled) setExtension('ready')
    }
    const observer = new MutationObserver(sync)
    observer.observe(el, { attributes: true, attributeFilter: ['disabled'] })
    const reannounce = () => {
      if (el.disabled) activateOPButton()
    }
    window.addEventListener('focus', reannounce)
    document.addEventListener('visibilitychange', reannounce)
    return () => {
      for (const t of timers) clearTimeout(t)
      clearTimeout(giveUp)
      observer.disconnect()
      window.removeEventListener('focus', reannounce)
      document.removeEventListener('visibilitychange', reannounce)
      el.value = ''
      el.disabled = true
    }
  }, [request])

  // A click the extension answers moves focus (popup/dialog) or emits its own event.
  useEffect(() => () => clearTimeout(clickTimer.current), [])
  const watchClick = () => {
    clearTimeout(clickTimer.current)
    const answered = () => {
      clearTimeout(clickTimer.current)
      cleanup()
    }
    const cleanup = () => {
      window.removeEventListener('blur', answered)
      document.removeEventListener('visibilitychange', answered)
      document.removeEventListener('OPButtonEvent', answered)
    }
    window.addEventListener('blur', answered)
    document.addEventListener('visibilitychange', answered)
    document.addEventListener('OPButtonEvent', answered)
    clickTimer.current = setTimeout(() => {
      cleanup()
      setExtension('silent')
    }, 2500)
  }
  const wrapperProps = {
    // Disabled buttons swallow clicks; explain instead of doing nothing.
    onPointerDown: () => {
      if (button.current?.disabled) setExtension('missing')
    },
    onClickCapture: () => {
      if (button.current && !button.current.disabled) watchClick()
    },
  }
  return { button, extension, wrapperProps }
}

/** Native rendering of the package's extension-facing button; see docs/research/onepassword-recovery.md. */
export function RecoverySave({ codes, accountId, email }: RecoveryDetails) {
  const id = useId()
  const [copyState, setCopyState] = useState<'idle' | 'copying' | 'copied' | 'failed'>('idle')
  const request = encodeOPSaveRequest(recoverySaveRequest({ codes, accountId, email })) ?? ''
  const text = recoveryText({ codes, accountId, email })
  const { button, extension, wrapperProps } = useOnePasswordSave(request)
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset copy state for new codes
  useEffect(() => setCopyState('idle'), [request])

  return (
    <div className="grid gap-2">
      <div className="flex flex-wrap gap-2">
        <span className="inline-flex" {...wrapperProps}>
          <button
            ref={button}
            id={`${id}-onepassword`}
            type="button"
            className="vk-btn vk-btn--primary !min-h-11"
            data-onepassword-save-button=""
            data-onepassword-save-request=""
            data-onepassword-type="login"
            aria-describedby={`${id}-help`}
          >
            Save in 1Password
          </button>
        </span>
        <Button
          type="button"
          className="!min-h-11"
          icon={<Copy size={15} />}
          disabled={copyState === 'copying'}
          onClick={async () => {
            setCopyState('copying')
            try {
              if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable')
              await navigator.clipboard.writeText(text)
              setCopyState('copied')
            } catch {
              // Never surface errors from APIs or extensions that could contain secret text.
              setCopyState('failed')
            }
          }}
        >
          Copy codes
        </Button>
      </div>
      <p id={`${id}-help`} className="m-0 text-[14px] text-text-3">
        Save opens the 1Password browser extension. If the button is unavailable, copy the codes and
        paste them into a login or secure note in your password manager. Finish saving there; Viko
        cannot confirm whether they were saved.
      </p>
      {extension === 'missing' || extension === 'silent' ? (
        <p role="alert" className="m-0 text-[14px] text-status-fail">
          {extension === 'missing'
            ? '1Password extension not detected in this browser.'
            : '1Password did not respond.'}{' '}
          Use Copy codes or Download and save them in 1Password by hand.
        </p>
      ) : null}
      {copyState === 'copied' ? (
        <p role="status" className="m-0 text-[14px] text-text-2">
          Copied. Paste into your password manager and save it there.
        </p>
      ) : null}
      {copyState === 'failed' ? (
        <p role="alert" className="m-0 text-[14px] text-status-fail">
          Could not copy. Select and copy the account id and codes above, or download them.
        </p>
      ) : null}
    </div>
  )
}
